// Copyright © Veeam Software Group GmbH

import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { BodyLayout, SplitLayout } from '@veeam/components';
import { map } from 'rxjs/operators';
import first from 'lodash/first';

import type { Node } from 'services';

import { ExploreGrid } from './components/ExploreGrid';
import { ExploreTree } from './components/ExploreTree';
import { ScopeHeader } from './components/ScopeHeader';
import { ExplorePageStore, ExplorePageStoreContext } from './stores/explore-page-store';
import { useExploreTreeModel } from './contexts/ExploreTreeProvider';


export const ExplorePage: React.FC = () => {
    const explorePageStore = useMemo(() => new ExplorePageStore(), []);
    const treeModel = useExploreTreeModel();
    const onNodeChanged = treeModel.asObservable().pipe(map((data) => {
        const id = first(data.selection);
        return id && treeModel.getItemById(id)?.model;
    }));
    const [node, setNode] = useState<Node>();
    const reactKey = node ? node.product + node.uniqId : undefined;

    useEffect(() => {
        const subscription = onNodeChanged.subscribe(node => setNode(node));

        return () => subscription.unsubscribe();
    }, []);

    useEffect(() => () => explorePageStore.destroy(), []);

    return (
        <ExplorePageStoreContext.Provider value={explorePageStore}>
            <ScopeHeader />

            <BodyLayout>
                <SplitLayout
                    start={
                        <ExploreTree />
                    }
                    startConfig={{ size: 0.15, minSize: 0.15 }}
                    end={
                        <ExploreGrid key={reactKey} />
                    }
                    endConfig={{ size: 0.85, minSize: 0.5 }}
                    data-selector-id='explore-page-split-layout'
                />
            </BodyLayout>
        </ExplorePageStoreContext.Provider>
    );
};
